import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, where, getDocs, doc, getDoc, setDoc } from '../firebaseConfig';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import Card from '@mui/material/Card';
import MDBox from '../components/MDBox';
import Divider from '@mui/material/Divider';
import TimelineIcon from '@mui/icons-material/Timeline';
import MaterialTooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import MDTypography from '../components/MDTypography';
import { useTheme } from '@mui/material/styles';
import { usePeriod } from '../components/PeriodContext';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const getWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  return Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
};

const TeamConsistencyScoreOverall = ({ teamId, userIDs }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [monthlyConsistencyScores, setMonthlyConsistencyScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchTeamConsistencyScores = async () => {
      setLoading(true);
      if (!teamId || !userIDs || userIDs.length === 0) {
        console.log('TeamConsistencyScoreOverall: Missing teamId or userIDs', { teamId, userIDs });
        setMonthlyConsistencyScores([]);
        setHasData(false);
        setLoading(false);
        return;
      }

      try {
        const oneEightyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 180))
          .toISOString()
          .split('T')[0]; // e.g., "2025-02-21"
        const sevenDaysAgo = new Date(new Date().setDate(new Date().getDate() - 7));

        // Aggregate consistency scores across all users
        let aggregatedConsistencyScores = Array(6).fill(0);
        let userCountWithData = 0;

        for (const userId of userIDs) {
          const userDataRef = doc(db, 'teamData', teamId, 'users', userId);
          const userDataDoc = await getDoc(userDataRef);

          let monthlyConsistencyScores = Array(6).fill(0);
          let hasCachedData = false;

          if (userDataDoc.exists()) {
            const userData = userDataDoc.data();
            if (userData.lastUpdated?.toDate() >= sevenDaysAgo && userData.monthlyConsistencyScores) {
              monthlyConsistencyScores = userData.monthlyConsistencyScores;
              hasCachedData = true;
              console.log(`TeamConsistencyScoreOverall: Using cached consistency scores for user ${userId}:`, monthlyConsistencyScores);
            }
          }

          if (!hasCachedData) {
            let allTrades = [];
            const accountsQuery = query(collection(db, `Users/${userId}/tradeAccounts`));
            const accountsDocs = await getDocs(accountsQuery);
            const tradeAccountIds = accountsDocs.docs.map((doc) => doc.id).concat('default');

            for (const accountId of tradeAccountIds) {
              const tradesQuery = query(
                collection(db, `Users/${userId}/tradeAccounts/${accountId}/trades`),
                where('status', '==', showMissedTrades ? 'Missed' : 'Closed'),
                where('date', '>=', oneEightyDaysAgo)
              );
              const tradesSnapshot = await getDocs(tradesQuery);
              const tradesData = tradesSnapshot.docs
                .map((doc) => ({
                  id: doc.id,
                  date: doc.data().date,
                  returns: parseFloat(doc.data().returns) || 0,
                  tags: Array.isArray(doc.data().tags) ? doc.data().tags : [],
                }))
                .filter((trade) => {
                  if (!trade.date || !/^\d{4}-\d{2}-\d{2}$/.test(trade.date)) {
                    console.warn(`Invalid date format for trade ${trade.id}:`, trade.date);
                    return false;
                  }
                  const tradeDate = new Date(trade.date);
                  if (isNaN(tradeDate.getTime())) {
                    console.warn(`Invalid date object for trade ${trade.id}:`, trade.date);
                    return false;
                  }
                  return true;
                });
              allTrades = allTrades.concat(tradesData);
            }

            // Apply period and tag filters
            const filteredTrades = filterTrades(allTrades);

            // Aggregate by month
            const monthlyWins = Array(6).fill(0);
            const monthlyDailyReturns = Array(6).fill().map(() => ({}));

            filteredTrades.forEach((trade) => {
              if (trade.returns <= 0) return;
              const tradeDate = new Date(trade.date);
              const monthIndex = Math.floor((new Date() - tradeDate) / (1000 * 60 * 60 * 24 * 30));
              if (monthIndex >= 0 && monthIndex < 6) {
                const dateKey = trade.date;
                if (!monthlyDailyReturns[monthIndex]) {
                  monthlyDailyReturns[monthIndex] = {};
                }
                monthlyDailyReturns[monthIndex][dateKey] = (monthlyDailyReturns[monthIndex][dateKey] || 0) + trade.returns;
                monthlyWins[monthIndex] += trade.returns;
              }
            });

            monthlyConsistencyScores = monthlyWins.map((totalWins, idx) => {
              if (totalWins === 0) return 0;
              const dailyReturns = monthlyDailyReturns[idx] || {};
              const mostProfitableDay = Object.values(dailyReturns).reduce((max, curr) => Math.max(max, curr || 0), 0);
              const consistency = 100 - (mostProfitableDay / totalWins) * 100;
              return isNaN(consistency) ? 0 : Math.max(0, Math.min(100, parseFloat(consistency.toFixed(2))));
            });

            await setDoc(userDataRef, {
              monthlyConsistencyScores,
              lastUpdated: new Date(),
              period: 'past 180 days',
            }, { merge: true });
            console.log(`TeamConsistencyScoreOverall: Stored consistency scores for user ${userId} in teamData/${teamId}/users/${userId}:`, monthlyConsistencyScores);
          }

          // Aggregate for team-wide display
          if (monthlyConsistencyScores.some(score => score > 0)) {
            userCountWithData++;
            monthlyConsistencyScores.forEach((score, idx) => {
              aggregatedConsistencyScores[idx] += score;
            });
          }
        }

        // Average across users
        const teamConsistencyScores = aggregatedConsistencyScores.map((sum) =>
          userCountWithData > 0 ? (sum / userCountWithData).toFixed(2) : 0
        );

        setMonthlyConsistencyScores(teamConsistencyScores);
        setHasData(teamConsistencyScores.some(score => score > 0));
        console.log('TeamConsistencyScoreOverall: Aggregated team consistency scores:', teamConsistencyScores);
      } catch (error) {
        console.error('TeamConsistencyScoreOverall: Error fetching trades:', error);
        setMonthlyConsistencyScores([]);
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamConsistencyScores();
  }, [teamId, userIDs, showMissedTrades, selectedPeriod, selectedYear, selectedMonth, selectedTag]);

  const filterTrades = (trades) => {
    return trades.filter((trade) => {
      const tradeDate = trade.date ? new Date(trade.date) : null;
      if (!tradeDate || isNaN(tradeDate.getTime())) return false;
      const matchesPeriod = () => {
        if (selectedPeriod === 'today') {
          return tradeDate.toDateString() === new Date().toDateString();
        } else if (selectedPeriod === 'this-week') {
          return getWeek(tradeDate) === getWeek(new Date()) && tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'this-month') {
          return tradeDate.getMonth() === new Date().getMonth() && tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'this-year') {
          return tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'pick-month') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-year') {
          return tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-month-year') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        }
        return true; // all-time
      };
      const matchesTag = selectedTag === 'all' || (Array.isArray(trade.tags) && trade.tags.includes(selectedTag));
      return matchesPeriod() && matchesTag;
    });
  };

  const getMonthLabels = () => {
    const labels = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      labels.push(date.toLocaleString('default', { month: 'short', year: 'numeric' })); // e.g., "Dec 2024"
    }
    return labels;
  };

  const data = {
    labels: getMonthLabels(),
    datasets: [
      {
        label: 'Team Consistency Score (%)',
        data: monthlyConsistencyScores,
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: { ticks: { autoSkip: true, maxTicksLimit: 6, maxRotation: 45, minRotation: 45 } },
      y: { beginAtZero: true, min: 0, max: 100, ticks: { stepSize: 10 } },
    },
    plugins: {
      legend: { display: true },
      tooltip: { callbacks: { label: (context) => `Consistency Score: ${context.parsed.y.toFixed(1)}%` } },
    },
  };

  const tooltipContent = 'Team Consistency Score shows the percentage of winning trades not attributed to the most profitable day, averaged across all team members over the past 180 days, aggregated monthly, updated weekly.';

  if (loading) {
    return (
      <Card style={{ minHeight: '300px' }}>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
          <MDTypography variant="caption" color="text">
            Loading trades...
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  if (!hasData) {
    return (
      <Card style={{ minHeight: '300px' }}>
        <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
          <MDBox display="flex" justifyContent="space-between">
            <MDBox
              variant="gradient"
              bgColor="light"
              color="dark"
              coloredShadow="light"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="4rem"
              height="4rem"
              mt={-3}
            >
              <TimelineIcon fontSize="medium" color="inherit" />
            </MDBox>
            <MDBox textAlign="right" lineHeight={1.25}>
              <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
                Team Consistency Score (All Members & Accounts)
              </MDTypography>
              <MaterialTooltip
                title={tooltipContent}
                placement="bottom-end"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      border: `1px solid ${theme.palette.divider}`,
                      p: 1.5,
                      maxWidth: '250px',
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                    },
                  },
                }}
              >
                <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
              </MaterialTooltip>
            </MDBox>
          </MDBox>
          <Divider />
          <MDBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            textAlign="center"
            px={2}
          >
            <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
            <MDTypography variant="h6" color="text">
              No consistency score data available
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Team members need to complete winning trades to see consistency analysis
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <TimelineIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Team Consistency Score (All Members & Accounts)
            </MDTypography>
            <MaterialTooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </MaterialTooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox height="100%">
          <Line data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

TeamConsistencyScoreOverall.propTypes = {
  teamId: PropTypes.string.isRequired,
  userIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default TeamConsistencyScoreOverall;